/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module AviComponentRenderingModule */

import {
    Component,
    ComponentRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    reflectComponentType,
    SimpleChanges,
    Type,
    ViewContainerRef,
} from '@angular/core';

import { each } from 'underscore';

/**
 * @description
 *     Used to render a component along with componentBindings. This component should be deprecatable once Angular has
 *     been upgraded to 16+ which supports ngComponentOutlet.
 * @author alextsg
 */
@Component({
    selector: 'avi-component-outlet',
    template: '',
})
export class AviComponentOutletComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public component: Type<Component>;

    @Input()
    public componentBindings: Record<string, unknown> = {};

    /**
     * If a dynamic component is rendered as part of an ngFor, and the component contains ngModels, we need to ensure
     * that the ngModels are unique. An optional index field is passed to the rendered component for uniqueness.
     */
    @Input()
    public index ? = 0;

    /**
     * Relates to the index @Input() binding. If the dynamic component expects a unique index but the name of the
     * binding is different (ex. matchIndex) the indexAlias can be used to change the binding name of the index passed
     * in.
     */
    @Input()
    public indexAlias ? = 'index';

    private viewContainerRef = inject(ViewContainerRef);

    private componentRef: ComponentRef<any>;

    /**
     * @override
     * Create an instance of the dynamic component in the viewContainer ref and attach component bindings.
     */
    public ngOnInit(): void {
        this.componentRef = this.viewContainerRef.createComponent(this.component);

        each(this.componentBindings, (prop, key): void => {
            if (this.componentRef.instance[key] instanceof EventEmitter) {
                this.componentRef.instance[key].subscribe(($event: unknown) => {
                    if (typeof prop === 'function') {
                        prop($event);
                    }
                });
            } else if (this.hasBinding(key)) {
                this.componentRef.setInput(key, prop);
            }
        });

        if (this.hasBinding(this.indexAlias)) {
            this.componentRef.setInput(this.indexAlias, this.index ?? 0);
        }
    }

    /**
     * @override
     * On changes to componentBindings, if the value or reference has changed, update the dynamic component's binding
     * values.
     */
    public ngOnChanges(changes: SimpleChanges): void {
        const { componentBindings, index } = changes;

        if (componentBindings && !componentBindings.firstChange) {
            const { currentValue = {} } = componentBindings;

            each(currentValue, (prop, key): void => {
                // Only update @Input() bindings, ignore @Output() EventEmitters.
                if (!(this.componentRef.instance[key] instanceof EventEmitter)) {
                    // Only update if values and references have changed.
                    if (prop !== this.componentRef.instance[key] && this.hasBinding(key)) {
                        this.componentRef.setInput(key, prop);
                    }
                }
            });
        }

        if (index && !index.firstChange) {
            if (this.hasBinding(this.indexAlias)) {
                this.componentRef.setInput(this.indexAlias, this.index ?? 0);
            }
        }
    }

    /**
     * @override
     * Destroy all components in the viewContainerRef.
     */
    public ngOnDestroy(): void {
        this.viewContainerRef.clear();
    }

    /**
     * Return true if the component has the binding defined.
     */
    private hasBinding(bindingName: string): boolean {
        const mirror = reflectComponentType(this.component);

        return mirror.inputs.some(input => input.propName === bindingName);
    }
}
